"use client";

import { useProfileBalance, useWallet, useClient } from "@getpara/react-sdk";
import { useWallet as useSolanaWallet } from "@solana/wallet-adapter-react";
import { TokenBalance } from "@/constants/types";
import { isNativeToken } from "@/constants/send-modal-helpers";
import { useEffect, useMemo, useRef, useState } from "react";
import { Connection, PublicKey } from "@solana/web3.js";
import { TOKEN_PROGRAM_ID, TOKEN_2022_PROGRAM_ID } from "@solana/spl-token";
import { useAllFeaturedProjects } from "@/hooks/use-featured-projects";
import { useAccount as useWagmiAccount, useConfig } from "wagmi";
import { getBalance, multicall } from "@wagmi/core";
import { Address, zeroAddress, erc20Abi, formatUnits } from "viem";
import { useTokenStore } from "@/lib/zustand";
import { ChainType } from "@lifi/sdk";
import { useOnChainBalanceOverrides } from "@/lib/on-chain-balance-overrides";
import { mobileDebugLog } from "@/lib/mobile-debug-log";
import { useRenderTickStore } from "@/lib/render-tick-store";
import { logDiagnostic } from "@/lib/silent-diagnostic";
import { useQueryClient, useQuery } from "@tanstack/react-query";

const SPL_FALLBACK_MIN_BALANCE = 0.001;
const MIN_USD_BALANCE = 0.01;
const RETRY_DELAY_MS = 2000;
const SOLANA_CHAIN_ID = 1151111081099710;
// Only poll ETH + BNB + Base by default. Other chains activated via useActiveEvmChainsStore
// when user selects them in swap UI. This prevents polling all 15 configured chains.
const CORE_EVM_NATIVE_CHAINS = [1, 56, 8453] as const;
const NATIVE_DECIMALS: Record<string, number> = {
  SOL: 9,
  ETH: 18,
  BNB: 18,
  MATIC: 18,
  POL: 18,
  AVAX: 18,
  FTM: 18,
  ONE: 18,
  CRO: 18,
  GLMR: 18,
};

// Fallback: resolve network name from evmChainId when metadata.name is undefined
const chainIdToNetworkName: Record<number, string> = {
  1: "Ethereum",
  56: "BNB",
  137: "Polygon",
  8453: "Base",
  42161: "Arbitrum",
  10: "Optimism",
  11155111: "Sepolia",
};

// Module-level singleton to prevent multiple hook instances from fetching simultaneously
// Exported so other modules can clear the cache (e.g., after swap completion)
export const evmFetchSingleton = {
  fetchId: 0,
  inFlight: false,
  lastFetchKey: "",
  cachedResult: null as TokenBalance[] | null,
  listeners: new Set<(tokens: TokenBalance[]) => void>(),
  refreshTrigger: 0,
  refreshListeners: new Set<() => void>(),
};

// Call this to force all useTokenBalances instances to re-fetch EVM balances
export const refreshEvmBalances = () => {
  evmFetchSingleton.refreshTrigger++;
  evmFetchSingleton.cachedResult = null;
  evmFetchSingleton.lastFetchKey = "";
  evmFetchSingleton.inFlight = false; // Reset to allow new fetch
  evmFetchSingleton.refreshListeners.forEach((listener) => listener());
};

// React Query key for Solana on-chain tokens - all components share this cache
export const SOLANA_TOKENS_QUERY_KEY = "solanaOnChainTokens";

// Call this to force all useTokenBalances instances to re-fetch Solana balances
// Uses React Query's cache invalidation - all components subscribed to the same queryKey
// will automatically refetch and re-render with fresh data
export const refreshSolanaBalances = (queryClient?: { invalidateQueries: (opts: { queryKey: string[] }) => Promise<void> | void }) => {
  if (queryClient) {
    // Invalidate the shared Solana tokens cache - triggers ONE refetch, all components update
    queryClient.invalidateQueries({ queryKey: [SOLANA_TOKENS_QUERY_KEY] });
    // Also invalidate swap form's direct Solana balance query to keep form in sync
    queryClient.invalidateQueries({ queryKey: ["solanaBalance"] });
  }
};

// CoinGecko price cache to handle 429 rate limiting
// Cache prices for 60 seconds so rate limit errors don't break balance display
const coinGeckoPriceCache = {
  prices: null as { ethereum?: number; binancecoin?: number } | null,
  lastFetchTime: 0,
  TTL_MS: 60000, // 60 seconds
};

// Module-level singleton for Jupiter token list (logo lookup for Solana tokens)
// Cached for 24 hours, fetched once per session
const jupiterTokenListCache = {
  tokens: null as Map<string, { logoURI: string; symbol: string; name: string }> | null,
  fetching: false,
  fetchPromise: null as Promise<void> | null, // Store promise so callers can await in-flight fetch
  fetchedAt: 0,
  TTL_MS: 24 * 60 * 60 * 1000, // 24 hours
  listeners: new Set<() => void>(),
};

// Fetch Jupiter token list (non-blocking, with 5-second timeout)
// Exported for use by other modules (e.g., burn page metadata)
// IMPORTANT: If a fetch is already in progress, callers await the same promise
export const fetchJupiterTokenList = async (): Promise<void> => {
  // Cache is valid, return immediately
  if (
    jupiterTokenListCache.tokens &&
    Date.now() - jupiterTokenListCache.fetchedAt < jupiterTokenListCache.TTL_MS
  ) {
    return;
  }

  // If already fetching, wait for that fetch to complete instead of returning immediately
  if (jupiterTokenListCache.fetching && jupiterTokenListCache.fetchPromise) {
    return jupiterTokenListCache.fetchPromise;
  }

  jupiterTokenListCache.fetching = true;

  // Store the promise so other callers can await it
  jupiterTokenListCache.fetchPromise = (async () => {
    try {
      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), 10000); // 10-second timeout for reliability

      const res = await fetch("https://cache.jup.ag/tokens", {
        signal: controller.signal,
        headers: { Accept: "application/json" },
      });
      clearTimeout(timeout);

      if (!res.ok) {
        console.warn("[Jupiter cache] Fetch failed:", res.status);
        return;
      }

      const tokens: Array<{ address?: string; logoURI?: string; symbol?: string; name?: string }> =
        await res.json();
      const tokenMap = new Map<string, { logoURI: string; symbol: string; name: string }>();

      for (const token of tokens) {
        if (token.address && token.logoURI) {
          tokenMap.set(token.address, {
            logoURI: token.logoURI,
            symbol: token.symbol || "",
            name: token.name || "",
          });
        }
      }

      jupiterTokenListCache.tokens = tokenMap;
      jupiterTokenListCache.fetchedAt = Date.now();
      console.log(`[Jupiter cache] Loaded ${tokenMap.size} tokens with logos`);

      // Notify listeners that Jupiter list is ready
      jupiterTokenListCache.listeners.forEach((listener) => listener());
    } catch (error) {
      console.warn("[Jupiter cache] Fetch error:", error);
      // Failed to fetch (timeout or network error) - gracefully fall through
      // Portfolio will use AvatarFallback for tokens without logos
    } finally {
      jupiterTokenListCache.fetching = false;
      jupiterTokenListCache.fetchPromise = null;
    }
  })();

  return jupiterTokenListCache.fetchPromise;
};

/**
 * Get token metadata from the shared Jupiter token list cache.
 * Ensures the cache is loaded before lookup.
 * Used by burn page and other modules that need Solana token metadata.
 *
 * @param mintAddress - Solana token mint address
 * @returns Token metadata { logoURI, symbol, name } or null if not found
 */
export async function getJupiterTokenMetadata(
  mintAddress: string
): Promise<{ logoURI: string; symbol: string; name: string } | null> {
  // Ensure cache is loaded
  await fetchJupiterTokenList();

  // Look up from cache
  const token = jupiterTokenListCache.tokens?.get(mintAddress);
  if (token?.logoURI) {
    return token;
  }

  return null;
}

/**
 * Get the entire Jupiter token list cache (Map of mint -> metadata).
 * Triggers fetch if cache is empty/stale. Non-blocking for immediate access.
 * Returns the current cache state (may be null if not yet loaded).
 */
export function getJupiterTokenListCache(): Map<string, { logoURI: string; symbol: string; name: string }> | null {
  // Trigger fetch if needed (non-blocking)
  fetchJupiterTokenList();
  return jupiterTokenListCache.tokens;
}

/**
 * Check if the Jupiter token list cache is loaded and ready.
 */
export function isJupiterCacheReady(): boolean {
  return jupiterTokenListCache.tokens !== null &&
    Date.now() - jupiterTokenListCache.fetchedAt < jupiterTokenListCache.TTL_MS;
}

// Look up Solana token logo from featured projects or Jupiter token list
const lookupSolanaTokenLogo = (
  mintAddress: string,
  featuredProjects: { tokenAddress: string; tokenLogoURI: string | null; tokenSymbol: string | null; tokenName: string | null; chainId: number }[] | undefined,
): { logoURI: string; symbol?: string; name?: string } => {
  // Check Jupiter for symbol/name (fallback if featured project doesn't have them)
  const jupiterToken = jupiterTokenListCache.tokens?.get(mintAddress);

  // Priority 1: Featured project (DB has logo, symbol, name)
  if (featuredProjects) {
    const featured = featuredProjects.find(
      (p) => p.chainId === SOLANA_CHAIN_ID && p.tokenAddress === mintAddress
    );
    if (featured?.tokenLogoURI) {
      return {
        logoURI: featured.tokenLogoURI,
        // Use featured project's symbol/name, fall back to Jupiter if null
        symbol: featured.tokenSymbol || jupiterToken?.symbol,
        name: featured.tokenName || jupiterToken?.name,
      };
    }
  }

  // Priority 2: Jupiter token list (logo + symbol + name)
  if (jupiterToken?.logoURI) {
    return jupiterToken;
  }

  // Priority 3: Empty (triggers AvatarFallback)
  return { logoURI: "" };
};

const shortenAddress = (address: string) => {
  if (address.length <= 8) return address;
  return `${address.slice(0, 4)}...${address.slice(-4)}`;
};

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

const isRateLimitError = (error: unknown): boolean => {
  const message = (error as any)?.message?.toString?.().toLowerCase?.() || "";
  return (
    message.includes("429") ||
    message.includes("too many requests") ||
    message.includes("rate limit")
  );
};

const withRetry = async <T,>(
  fn: () => Promise<T>,
  maxAttempts = 2,
): Promise<T | undefined> => {
  for (let attempt = 1; attempt <= maxAttempts; attempt += 1) {
    try {
      return await fn();
    } catch (error) {
      if (isRateLimitError(error)) {
        console.warn(
          "Rate limited by RPC (429). Skipping retries for this call.",
        );
        return undefined;
      }

      if (attempt >= maxAttempts) {
        return undefined;
      }

      await sleep(RETRY_DELAY_MS);
    }
  }

  return undefined;
};

// Use shared price fetcher (with caching) from lib/prices.ts
import { fetchSolanaTokenPrices } from "@/lib/prices";

// Alias for backward compatibility - the function name is a misnomer
// (it uses DexScreener, not Jupiter) but changing all callsites is out of scope
const fetchJupiterPrices = fetchSolanaTokenPrices;

const normalizeParaQuantity = ({
  quantity,
  decimals,
  valueUSD,
}: {
  quantity: number | string;
  decimals: number;
  valueUSD: number;
}) => {
  const numericQuantity = Number(quantity);

  if (
    !Number.isFinite(numericQuantity) ||
    numericQuantity === 0 ||
    decimals <= 0
  ) {
    return numericQuantity;
  }

  const quantityAsString = String(quantity).trim();
  if (/[.eE]/.test(quantityAsString)) {
    return numericQuantity;
  }

  const normalizedQuantity = numericQuantity / Math.pow(10, decimals);

  if (valueUSD <= 0 || normalizedQuantity <= 0) {
    return numericQuantity;
  }

  const rawImpliedPrice = valueUSD / numericQuantity;
  const normalizedImpliedPrice = valueUSD / normalizedQuantity;
  const looksLikeRawUnits =
    numericQuantity >= Math.pow(10, decimals) &&
    rawImpliedPrice < 1e-9 &&
    normalizedImpliedPrice >
      rawImpliedPrice * Math.pow(10, Math.min(decimals, 12)) * 0.1;

  return looksLikeRawUnits ? normalizedQuantity : numericQuantity;
};

// Query function for fetching Solana on-chain tokens
// This is extracted from the old effect-based approach to work with React Query
interface SolanaTokensQueryResult {
  tokens: TokenBalance[];
  nativeSolBalance: number | null;
  pricesByMint: Record<string, number>;
}

const fetchSolanaOnChainTokens = async (
  solanaWalletAddress: string,
  featuredProjects: { tokenAddress: string; tokenLogoURI: string | null; tokenSymbol: string | null; tokenName: string | null; chainId: number }[] | undefined,
): Promise<SolanaTokensQueryResult> => {
  const solanaRpcUrl =
    typeof window !== "undefined"
      ? `${window.location.origin}/api/rpc?chain=solana`
      : "https://api.mainnet-beta.solana.com";
  const connection = new Connection(solanaRpcUrl, {
    commitment: "confirmed",
    disableRetryOnRateLimit: true,
  });
  const walletPublicKey = new PublicKey(solanaWalletAddress);

  // Fetch native SOL balance
  const nativeSolBalanceRaw = await withRetry(
    () => connection.getBalance(walletPublicKey),
    2,
  );
  const nativeSolBalance = nativeSolBalanceRaw !== undefined
    ? nativeSolBalanceRaw / 1e9
    : null;

  // Fetch all SPL token accounts (both classic and Token-2022)
  const [classicAccounts, token2022Accounts] = await Promise.all([
    withRetry(
      () =>
        connection.getParsedTokenAccountsByOwner(walletPublicKey, {
          programId: TOKEN_PROGRAM_ID,
        }),
      2,
    ),
    withRetry(
      () =>
        connection.getParsedTokenAccountsByOwner(walletPublicKey, {
          programId: TOKEN_2022_PROGRAM_ID,
        }),
      2,
    ),
  ]);

  // Merge accounts from both programs
  const allAccounts = [
    ...(classicAccounts?.value ?? []),
    ...(token2022Accounts?.value ?? []),
  ];

  if (allAccounts.length === 0) {
    return { tokens: [], nativeSolBalance, pricesByMint: {} };
  }

  const parsedAccounts = allAccounts
    .map(({ account }) => {
      const parsedData = (account.data as any)?.parsed?.info;
      const tokenAmount = parsedData?.tokenAmount;
      const mintAddress = parsedData?.mint as string | undefined;
      const balance = Number(
        tokenAmount?.uiAmountString || tokenAmount?.uiAmount || 0,
      );
      const decimals = tokenAmount?.decimals ?? 0;
      return { mintAddress, balance, decimals };
    })
    .filter(
      (token) =>
        !!token.mintAddress &&
        token.balance > SPL_FALLBACK_MIN_BALANCE &&
        // Exclude NFTs: decimals=0 with balance=1
        !(token.decimals === 0 && token.balance === 1),
    ) as Array<{ mintAddress: string; balance: number; decimals: number }>;

  const uniqueMints = [...new Set(parsedAccounts.map((t) => t.mintAddress))];
  const pricesByMint = await fetchJupiterPrices(uniqueMints);

  const parsedTokens: TokenBalance[] = parsedAccounts
    .map((token) => {
      const price = pricesByMint[token.mintAddress] || 0;
      const balanceUSD = token.balance * price;

      if (price > 0 && balanceUSD < MIN_USD_BALANCE) {
        return null;
      }

      const fallbackSymbol = shortenAddress(token.mintAddress);
      const logoLookup = lookupSolanaTokenLogo(token.mintAddress, featuredProjects);

      return {
        symbol: logoLookup.symbol || fallbackSymbol,
        name: logoLookup.name || fallbackSymbol,
        balance: token.balance.toString(),
        balanceUSD,
        price,
        change24h: 0,
        change24hPercent: 0,
        address: token.mintAddress,
        decimals: token.decimals,
        isNative: false,
        network: "Solana",
        logoURI: logoLookup.logoURI,
      };
    })
    .filter(Boolean) as TokenBalance[];

  return { tokens: parsedTokens, nativeSolBalance, pricesByMint };
};

export const useTokenBalances = (refreshKey = 0) => {
  const { data: profileBalance, isLoading: isLoadingProfileBalance } =
    useProfileBalance({});
  const { data: wallet } = useWallet();
  const { publicKey: solanaPublicKey } = useSolanaWallet();
  const { data: featuredProjects } = useAllFeaturedProjects();
  const { address: wagmiAddress } = useWagmiAccount();
  const wagmiConfig = useConfig();
  const { fromChain } = useTokenStore();
  const para = useClient();
  const onChainOverrides = useOnChainBalanceOverrides((s) => s.overrides);
  const renderTick = useRenderTickStore((s) => s.tick);
  const queryClient = useQueryClient();
  const [, forceRerender] = useState(0);

  // Part A: Explicit subscription to override store for mobile Safari re-render fix
  // Async zustand updates from fire-and-forget contexts don't reliably trigger re-renders on mobile
  useEffect(() => {
    const unsub = useOnChainBalanceOverrides.subscribe(() => {
      forceRerender((n) => n + 1);
    });
    return unsub;
  }, []);

  // EVM tokens still use local state (not migrated to React Query yet)
  const [onChainEvmTokens, setOnChainEvmTokens] = useState<TokenBalance[]>([]);
  // Jupiter prices from Solana query - stored in state for fallback pricing
  const [jupiterFallbackPrices, setJupiterFallbackPrices] = useState<
    Record<string, number>
  >({});
  const lastJupiterRequestKeyRef = useRef<string>("");
  // Track last logged override state per token to avoid spamming debug logs
  const lastLoggedOverrideRef = useRef<Map<string, string>>(new Map());

  // Track refresh trigger from refreshEvmBalances() calls
  const [evmRefreshTrigger, setEvmRefreshTrigger] = useState(
    () => evmFetchSingleton.refreshTrigger
  );

  // Subscribe to refresh events
  useEffect(() => {
    const listener = () => setEvmRefreshTrigger(evmFetchSingleton.refreshTrigger);
    evmFetchSingleton.refreshListeners.add(listener);
    return () => {
      evmFetchSingleton.refreshListeners.delete(listener);
    };
  }, []);

  // Track when Jupiter token list is ready (for logo lookup)
  const [jupiterListReady, setJupiterListReady] = useState(
    () => jupiterTokenListCache.tokens !== null
  );

  // Fetch Jupiter token list on mount (non-blocking)
  // Don't block initial portfolio load - show portfolio first, populate logos when fetch completes
  useEffect(() => {
    const listener = () => setJupiterListReady(true);
    jupiterTokenListCache.listeners.add(listener);

    // Trigger fetch (async, non-blocking)
    fetchJupiterTokenList();

    return () => {
      jupiterTokenListCache.listeners.delete(listener);
    };
  }, []);

  // Use fromChain.chainType as primary signal for which address to use
  const isSolanaMode = fromChain.chainType === ChainType.SVM;

  // Cache email EVM address - fetch once and keep it (Para SDK can lose it during switches)
  const cachedEmailEvmAddressRef = useRef<string | null>(null);
  const [emailEvmAddress, setEmailEvmAddress] = useState<string | null>(() => {
    // Always try to get EVM address on first render (regardless of mode)
    // We cache it so it's available when user toggles to EVM
    try {
      const evmWallets = para?.getWalletsByType?.("EVM") || [];
      const addr = evmWallets[0]?.address ?? null;
      if (addr) cachedEmailEvmAddressRef.current = addr;
      return addr;
    } catch {
      return null;
    }
  });

  const isEmailOrSocialWallet = !!(
    (wallet as any)?.email ||
    (wallet as any)?.userId ||
    (wallet as any)?.externalId
  );

  const solanaWalletAddress = isSolanaMode
    ? (wallet?.address && wallet?.type === "SOLANA" ? wallet.address : solanaPublicKey?.toBase58() || null)
    : null;

  useEffect(() => {
    // For external wallets (wagmiAddress exists), we don't need emailEvmAddress
    if (wagmiAddress || !isEmailOrSocialWallet) {
      return;
    }
    // Always try to fetch/cache EVM address for email wallets (regardless of mode)
    // This ensures the address is ready when user toggles to EVM
    try {
      const evmWallets = para?.getWalletsByType?.("EVM") || [];
      const freshAddr = evmWallets[0]?.address;
      if (freshAddr) {
        cachedEmailEvmAddressRef.current = freshAddr;
        setEmailEvmAddress(freshAddr);
      } else if (cachedEmailEvmAddressRef.current) {
        // Para SDK returned empty, fall back to cached
        setEmailEvmAddress(cachedEmailEvmAddressRef.current);
      }
    } catch {
      // On error, try to use cached address
      if (cachedEmailEvmAddressRef.current) {
        setEmailEvmAddress(cachedEmailEvmAddressRef.current);
      }
    }
  }, [wagmiAddress, isEmailOrSocialWallet, wallet?.type]);

  const evmWalletAddress = !isSolanaMode
    ? (wagmiAddress || emailEvmAddress || null)
    : null;

  // React Query for Solana on-chain tokens - all components share this cache
  // When any component calls refreshSolanaBalances(), the cache is invalidated
  // and ALL subscribed components refetch ONCE (shared) and re-render together
  const solanaTokensQuery = useQuery({
    queryKey: [SOLANA_TOKENS_QUERY_KEY, solanaWalletAddress],
    queryFn: async () => {
      if (!solanaWalletAddress) {
        return { tokens: [], nativeSolBalance: null, pricesByMint: {} };
      }
      return fetchSolanaOnChainTokens(solanaWalletAddress, featuredProjects?.dbProjects);
    },
    enabled: !!solanaWalletAddress,
    staleTime: 30000, // 30 seconds - data considered fresh
    gcTime: 300000, // 5 minutes - keep in cache after unmount
    refetchOnWindowFocus: false, // Don't spam RPC on tab switch
  });

  // Extract data from query for use throughout the hook
  const onChainSolanaTokens = solanaTokensQuery.data?.tokens ?? [];
  const onChainNativeSolBalance = solanaTokensQuery.data?.nativeSolBalance ?? null;

  // Sync Jupiter prices from query result to state (for fallback pricing in other places)
  useEffect(() => {
    if (solanaTokensQuery.data?.pricesByMint) {
      setJupiterFallbackPrices((current) => ({
        ...current,
        ...solanaTokensQuery.data.pricesByMint,
      }));
    }
  }, [solanaTokensQuery.data?.pricesByMint]);

  // Defensive: clear all balance caches whenever a wallet connects.
  // This protects against Para's backend or any persistent storage
  // serving stale data from a previous session/user.
  // Uses removeQueries (immediate delete) then invalidateQueries (force refetch),
  // so there's no window where stale data is displayed.
  useEffect(() => {
    if (solanaWalletAddress || evmWalletAddress) {
      queryClient.removeQueries({ queryKey: ["useProfileBalance"] });
      queryClient.removeQueries({ queryKey: ["balance"] });
      queryClient.removeQueries({ queryKey: ["profileBalance"] });

      // Force fresh fetch
      queryClient.invalidateQueries({ queryKey: ["useProfileBalance"] });
    }
  }, [solanaWalletAddress, evmWalletAddress, queryClient]);

  // Patch logos onto existing Solana tokens when featuredProjects or Jupiter list loads AFTER initial fetch
  // This fixes the race condition where on-chain fetch completes before logo sources are ready
  // Track which source states we've tried for each token to avoid infinite loops while still retrying new sources
  const patchAttemptStateRef = useRef<{ featuredReady: boolean; jupiterReady: boolean }>({
    featuredReady: false,
    jupiterReady: false,
  });
  useEffect(() => {
    const dbProjects = featuredProjects?.dbProjects;
    const featuredReady = (dbProjects?.length ?? 0) > 0;
    const jupiterReady = jupiterListReady && jupiterTokenListCache.tokens !== null;

    // Check if we have a NEW source available that we haven't tried yet
    const hasNewSource =
      (featuredReady && !patchAttemptStateRef.current.featuredReady) ||
      (jupiterReady && !patchAttemptStateRef.current.jupiterReady);

    // Debug logging for mobile diagnosis
    mobileDebugLog("PATCH-CHECK", "effect-run", {
      featuredReady,
      jupiterReady,
      hasNewSource,
      tokensCount: onChainSolanaTokens.length,
      prevState: patchAttemptStateRef.current,
    });

    // Need at least one logo source, some tokens, and a new source to try
    if (!featuredReady && !jupiterReady) return;
    if (onChainSolanaTokens.length === 0) return;
    if (!hasNewSource) return;

    // Update what we've tried
    patchAttemptStateRef.current = { featuredReady, jupiterReady };

    // Find tokens missing logos
    const tokensMissingLogos = onChainSolanaTokens.filter(token => !token.logoURI);
    if (tokensMissingLogos.length === 0) {
      mobileDebugLog("PATCH-SKIP", "no-missing-logos", { tokensCount: onChainSolanaTokens.length });
      return;
    }

    mobileDebugLog("PATCH-START", "patching", { missingCount: tokensMissingLogos.length });

    // Check if we can resolve any logos
    let needsUpdate = false;
    const updatedTokens = onChainSolanaTokens.map(token => {
      // Skip if already has logo
      if (token.logoURI) return token;

      // Priority 1: Featured projects (DB-cached logo)
      if (featuredReady && dbProjects) {
        const featured = dbProjects.find(
          p => p.chainId === SOLANA_CHAIN_ID && p.tokenAddress === token.address
        );
        if (featured?.tokenLogoURI) {
          needsUpdate = true;
          if (process.env.NODE_ENV !== "production") {
            console.log(`[Logo Patch] ${token.symbol}: patching from featuredProjects`);
          }
          mobileDebugLog("PATCH-FOUND", token.symbol, { source: "featured", logo: featured.tokenLogoURI.slice(0, 50) });
          return { ...token, logoURI: featured.tokenLogoURI };
        }
      }

      // Priority 2: Jupiter token list
      if (jupiterReady) {
        const jupiterToken = jupiterTokenListCache.tokens?.get(token.address);
        if (jupiterToken?.logoURI) {
          needsUpdate = true;
          if (process.env.NODE_ENV !== "production") {
            console.log(`[Logo Patch] ${token.symbol}: patching from Jupiter`);
          }
          mobileDebugLog("PATCH-FOUND", token.symbol, { source: "jupiter", logo: jupiterToken.logoURI.slice(0, 50) });
          return {
            ...token,
            logoURI: jupiterToken.logoURI,
            // Also update symbol/name if we had fallback values
            symbol: token.symbol?.startsWith("0x") || (token.symbol?.length ?? 0) > 10
              ? jupiterToken.symbol || token.symbol
              : token.symbol,
            name: token.name?.startsWith("0x") || (token.name?.length ?? 0) > 20
              ? jupiterToken.name || token.name
              : token.name,
          };
        }
      }

      return token;
    });

    if (needsUpdate && solanaWalletAddress) {
      mobileDebugLog("PATCH-APPLY", "updating-state", { count: updatedTokens.filter(t => t.logoURI).length });
      // Update the React Query cache with patched tokens
      queryClient.setQueryData(
        [SOLANA_TOKENS_QUERY_KEY, solanaWalletAddress],
        (oldData: SolanaTokensQueryResult | undefined) => {
          if (!oldData) return oldData;
          return {
            ...oldData,
            tokens: updatedTokens,
          };
        }
      );
      // Force re-render for mobile Safari which sometimes doesn't re-render on state updates
      forceRerender((n) => n + 1);
    }
  }, [featuredProjects?.dbProjects, jupiterListReady, onChainSolanaTokens, solanaWalletAddress, queryClient, forceRerender]);

  // Reset patch attempt state when wallet changes (new tokens will need patching)
  useEffect(() => {
    patchAttemptStateRef.current = { featuredReady: false, jupiterReady: false };
  }, [solanaWalletAddress, refreshKey]);

  useEffect(() => {
    const currentLength = featuredProjects?.formattedProjects?.length ?? 0;
    const fetchKey = `${evmWalletAddress}-${currentLength}`;

    // If no wallet or no projects, clear and return
    if (!evmWalletAddress || !currentLength) {
      setOnChainEvmTokens([]);
      return;
    }

    // If we have cached results for this exact key, use them immediately
    if (evmFetchSingleton.lastFetchKey === fetchKey && evmFetchSingleton.cachedResult) {
      setOnChainEvmTokens(evmFetchSingleton.cachedResult);
      return;
    }

    // Register this instance to receive results
    const listener = (tokens: TokenBalance[]) => {
      setOnChainEvmTokens(tokens);
    };
    evmFetchSingleton.listeners.add(listener);

    // If a fetch is already in flight for this key, just wait for results
    if (evmFetchSingleton.inFlight && evmFetchSingleton.lastFetchKey === fetchKey) {
      return () => {
        evmFetchSingleton.listeners.delete(listener);
      };
    }

    // Start a new fetch
    evmFetchSingleton.inFlight = true;
    evmFetchSingleton.lastFetchKey = fetchKey;
    const fetchId = ++evmFetchSingleton.fetchId;

    const fetchFeaturedEvmTokenBalances = async () => {
      const evmFeaturedTokens = featuredProjects!.formattedProjects.filter(
        (project) =>
          project.chainId !== SOLANA_CHAIN_ID &&
          project.address &&
          project.address !== zeroAddress,
      );

      // Group tokens by chainId for batched multicall
      const tokensByChain = new Map<number, typeof evmFeaturedTokens>();
      for (const token of evmFeaturedTokens) {
        const existing = tokensByChain.get(token.chainId) || [];
        existing.push(token);
        tokensByChain.set(token.chainId, existing);
      }

      // Fetch balances using multicall (1 RPC call per chain instead of 1 per token)
      const balances: (TokenBalance | null)[] = [];

      for (const [chainId, tokens] of tokensByChain) {
        try {
          const contracts = tokens.map((token) => ({
            address: token.address as Address,
            abi: erc20Abi,
            functionName: "balanceOf" as const,
            args: [evmWalletAddress as Address],
          }));

          const results = await multicall(wagmiConfig, {
            contracts,
            chainId,
            allowFailure: true,
          });

          for (let i = 0; i < tokens.length; i++) {
            const project = tokens[i];
            const result = results[i];

            if (result.status === "failure" || !result.result) {
              balances.push(null);
              continue;
            }

            const rawBalance = result.result as bigint;
            if (rawBalance === BigInt(0)) {
              balances.push(null);
              continue;
            }

            const decimals = project.decimals ?? 18;
            const formatted = formatUnits(rawBalance, decimals);
            const numericBalance = Number(formatted);
            const numericPrice = Number(project.priceUSD || 0);

            balances.push({
              symbol: project.symbol,
              name: project.name,
              balance: formatted,
              balanceUSD: Number.isFinite(numericBalance * numericPrice)
                ? numericBalance * numericPrice
                : 0,
              price: Number.isFinite(numericPrice) ? numericPrice : 0,
              change24h: 0,
              change24hPercent: 0,
              address: project.address,
              decimals,
              isNative: false,
              network:
                chainIdToNetworkName[project.chainId] ||
                project.chainId.toString(),
              evmChainId: project.chainId,
              logoURI: project.logoURI || "",
            } satisfies TokenBalance);
          }
        } catch (error) {
          console.warn(
            `Failed to fetch EVM token balances for chain ${chainId} via multicall:`,
            error,
          );
          // On multicall failure, push null for all tokens on this chain
          for (let i = 0; i < tokens.length; i++) {
            balances.push(null);
          }
        }
      }

      // Fetch native token prices from CoinGecko with caching to handle 429 rate limiting
      let nativePrices: { ethereum?: number; binancecoin?: number } = {};
      const now = Date.now();
      const cacheValid = coinGeckoPriceCache.prices && (now - coinGeckoPriceCache.lastFetchTime < coinGeckoPriceCache.TTL_MS);

      if (cacheValid) {
        nativePrices = coinGeckoPriceCache.prices!;
      } else {
        try {
          const priceRes = await fetch(
            'https://api.coingecko.com/api/v3/simple/price?ids=ethereum,binancecoin&vs_currencies=usd'
          );
          if (priceRes.ok) {
            const priceData = await priceRes.json();
            nativePrices = {
              ethereum: priceData?.ethereum?.usd,
              binancecoin: priceData?.binancecoin?.usd,
            };
            // Cache successful response
            coinGeckoPriceCache.prices = nativePrices;
            coinGeckoPriceCache.lastFetchTime = now;
          } else if (priceRes.status === 429 && coinGeckoPriceCache.prices) {
            // Rate limited - use cached prices if available
            nativePrices = coinGeckoPriceCache.prices;
          }
        } catch {
          // CoinGecko fetch failed - use cached prices if available
          if (coinGeckoPriceCache.prices) {
            nativePrices = coinGeckoPriceCache.prices;
          }
        }
      }

      const nativeBalances = await Promise.all(
        CORE_EVM_NATIVE_CHAINS.map(async (chainId) => {
          try {
            const nativeBalance = await getBalance(wagmiConfig, {
              address: evmWalletAddress as Address,
              chainId,
            });

            if (!nativeBalance.value || nativeBalance.value === BigInt(0)) {
              return null;
            }

            const symbol = nativeBalance.symbol || "ETH";
            const nativeTokenAddress = zeroAddress;
            const isBnb = chainId === 56;
            const price = isBnb
              ? (nativePrices.binancecoin ?? 0)
              : (nativePrices.ethereum ?? 0);
            const numericBalance = parseFloat(nativeBalance.formatted);
            const balanceUSD = numericBalance * price;

            return {
              symbol,
              name:
                chainId === 56
                  ? "BNB"
                  : chainId === 8453
                    ? "Ethereum"
                    : "Ethereum",
              balance: nativeBalance.formatted,
              balanceUSD,
              price,
              change24h: 0,
              change24hPercent: 0,
              address: nativeTokenAddress,
              decimals: nativeBalance.decimals,
              isNative: true,
              network: chainIdToNetworkName[chainId] || chainId.toString(),
              evmChainId: chainId,
              logoURI:
                symbol === "BNB"
                  ? "https://tokens.1inch.io/0xbb4cdb9cbd36b01bd1cbaebf2de08d9173bc095c.png"
                  : "https://tokens.1inch.io/0xeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeee.png",
            } satisfies TokenBalance;
          } catch (error) {
            console.warn(
              `Failed to fetch native balance for chain ${chainId}:`,
              error,
            );
            return null;
          }
        }),
      );

      // Only broadcast if this is still the latest fetch
      if (fetchId === evmFetchSingleton.fetchId) {
        const result = [...balances, ...nativeBalances].filter(Boolean) as TokenBalance[];
        evmFetchSingleton.cachedResult = result;
        evmFetchSingleton.inFlight = false;
        // Broadcast to all listeners
        evmFetchSingleton.listeners.forEach(l => l(result));
      }
    };

    fetchFeaturedEvmTokenBalances();

    return () => {
      evmFetchSingleton.listeners.delete(listener);
    };
    // Note: Using .length instead of the array itself to avoid re-running on every render
    // Note: wagmiConfig excluded - it's a static config but returns new object refs on every render
    // evmRefreshTrigger forces re-fetch when refreshEvmBalances() is called
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [evmWalletAddress, featuredProjects?.formattedProjects?.length, evmRefreshTrigger]);

  const mergedTokens = useMemo(() => {
    try {
      const allTokens: TokenBalance[] = [];
      const wallets = profileBalance?.wallets || [];
      const isSolanaMode = fromChain.chainType === ChainType.SVM;

      // Build set of symbols we have on-chain data for (to filter $0.00 ghost entries)
      const onChainEvmSymbols = new Set(onChainEvmTokens.map(t => t.symbol));

      // Validate addresses match connected wallet. Para SDK's profileBalance query key
      // doesn't include wallet address, so stale data from a previous wallet connection
      // could leak through cached responses. This filter prevents cross-wallet data leakage.
      const filtered = wallets.filter((w: any) => {
        if (isSolanaMode) {
          return w.type === "SOLANA" && w.address === solanaWalletAddress;
        }
        return w.type === "EVM" && w.address?.toLowerCase() === evmWalletAddress?.toLowerCase();
      });

      for (const w of filtered) {
        const networks = w.networks || [];

        for (const n of networks) {
          const rawEvmChainId = n?.metadata?.evmChainId;
          const evmChainId = rawEvmChainId
            ? parseInt(rawEvmChainId, 10)
            : undefined;

          const networkName =
            (evmChainId && chainIdToNetworkName[evmChainId]) ||
            n?.metadata?.name ||
            (w.type === "SOLANA" ? "Solana" : "Ethereum");
          const assets = n.assets || [];

          for (const a of assets) {
            const symbol = a?.metadata?.symbol || a?.metadata?.name || "ASSET";
            const name =
              a?.metadata?.name || a?.metadata?.internalId || "Asset";
            const quantity = a?.quantity ?? 0;
            const valueUSD = a?.value?.value ?? 0;
            const contractAddress = a?.contractAddress || "";
            const logoURI = a?.metadata?.logoUrl || "";

            // Prefer on-chain decimals from SPL token accounts over Para metadata
            // Para metadata decimals can be wrong for some tokens
            const metadataDecimals = a?.metadata?.decimals;
            // Note: decimals can be 0 for some tokens, so check !== undefined/null, not > 0
            // Use network-appropriate fallback: 9 for Solana, 18 for EVM
            const isSolanaNetwork = w.type === "SOLANA" || networkName === "Solana";
            const defaultDecimals = isSolanaNetwork ? 9 : 18;
            const decimals =
              metadataDecimals !== undefined && metadataDecimals !== null
                ? metadataDecimals
                : (NATIVE_DECIMALS[symbol] ?? defaultDecimals);

            const normalizedQuantity = normalizeParaQuantity({
              quantity,
              decimals,
              valueUSD,
            });
            const isNative = isNativeToken(symbol);
            if (!contractAddress && !isNative) {
              continue;
            }

            // Skip $0.00 ghost entries from profileBalance when we have on-chain data for this symbol
            if (valueUSD === 0 && onChainEvmSymbols.has(symbol)) {
              continue;
            }

            // For native SOL: use on-chain balance as authoritative (overrides Para's cached value)
            const isNativeSol = symbol === "SOL" && isSolanaNetwork && isNative;
            let finalBalance = normalizedQuantity;
            let finalBalanceUSD = valueUSD;
            let finalPrice = normalizedQuantity > 0 ? valueUSD / normalizedQuantity : 0;

            if (isNativeSol && onChainNativeSolBalance !== null) {
              finalBalance = onChainNativeSolBalance;
              // Use Para's price if available, otherwise calculate from their USD/quantity
              finalPrice = normalizedQuantity > 0 ? valueUSD / normalizedQuantity : 0;
              finalBalanceUSD = finalBalance * finalPrice;

            }

            allTokens.push({
              symbol,
              name,
              balance: finalBalance.toString(),
              balanceUSD: finalBalanceUSD,
              price: finalPrice,
              change24h: 0,
              change24hPercent: 0,
              address: contractAddress,
              decimals: decimals,
              isNative: isNative,
              network: networkName,
              evmChainId:
                evmChainId && !isNaN(evmChainId) ? evmChainId : undefined,
              logoURI: logoURI,
            });
          }
        }
      }

      const paraSolanaMints = new Set(
        allTokens
          .filter((token) => token.network === "Solana" && token.address)
          .map((token) => `${token.network}-${token.address}`),
      );

      onChainSolanaTokens.forEach((token) => {
        const solanaKey = `${token.network}-${token.address}`;

        if (!paraSolanaMints.has(solanaKey)) {
          allTokens.push(token);
        } else {
          // Update existing token with on-chain authoritative values
          // Para SDK often lacks logos for newer tokens; on-chain lookup resolves from DB/Jupiter
          const existing = allTokens.find(
            (t) => t.address === token.address && t.network === "Solana",
          );
          if (existing) {
            // Always use on-chain decimals - they're authoritative from the token account
            // Note: decimals can be 0 for some tokens, so use !== undefined check
            if (token.decimals !== undefined && token.decimals !== null) {
              existing.decimals = token.decimals;
            }
            // Prefer on-chain resolved logo over Para's empty/missing logo
            if (token.logoURI && !existing.logoURI) {
              existing.logoURI = token.logoURI;
            }
            // Also update symbol/name if on-chain has better values (from Jupiter/featured)
            if (token.symbol && (!existing.symbol || existing.symbol.startsWith("0x") || existing.symbol.length > 10)) {
              existing.symbol = token.symbol;
            }
            if (token.name && (!existing.name || existing.name.startsWith("0x") || existing.name.length > 20)) {
              existing.name = token.name;
            }
          }
        }
      });

      if (!isSolanaMode) {
        onChainEvmTokens.forEach((token) => {
          const existingToken = allTokens.find(
            (existing) =>
              existing.evmChainId === token.evmChainId &&
              existing.address?.toLowerCase() === token.address?.toLowerCase(),
          );

          if (!existingToken) {
            allTokens.push(token);
          } else {
            // Update existing token with featured project logo if Para SDK lacks it
            if (token.logoURI && !existingToken.logoURI) {
              existingToken.logoURI = token.logoURI;
            }
          }
        });
      }

      // Post-merge deduplication: for duplicate address+chainId, keep the one with highest balanceUSD
      // This ensures ghost $0.00 entries from profileBalance are replaced by real on-chain data
      // BUG FIX: Use address (not symbol) as primary dedup key to prevent duplicates when
      // Para and on-chain have different symbols for the same token
      const dedupeMap = new Map<string, TokenBalance>();
      allTokens.forEach((token) => {
        const chainKey = token.evmChainId ?? token.network;
        // For non-native tokens, use address as key (more reliable than symbol)
        // For native tokens, use "native" as key since address varies (zeroAddress vs empty)
        const addressKey = token.isNative ? "native" : (token.address || "unknown");
        const key = `${addressKey}-${chainKey}`;
        const existing = dedupeMap.get(key);
        if (existing) {
          // Keep the one with higher balanceUSD (real pricing beats $0.00 ghost)
          // Also prefer the one with a proper symbol (not shortened address)
          const existingHasProperSymbol = existing.symbol && existing.symbol.length <= 10 && !existing.symbol.includes("...");
          const tokenHasProperSymbol = token.symbol && token.symbol.length <= 10 && !token.symbol.includes("...");

          if (token.balanceUSD > existing.balanceUSD) {
            // Merge: keep higher USD but preserve proper symbol if available
            const mergedToken = { ...token };
            if (existingHasProperSymbol && !tokenHasProperSymbol) {
              mergedToken.symbol = existing.symbol;
              mergedToken.name = existing.name || mergedToken.name;
              mergedToken.logoURI = existing.logoURI || mergedToken.logoURI;
            }
            dedupeMap.set(key, mergedToken);
          } else if (tokenHasProperSymbol && !existingHasProperSymbol) {
            // Token has better metadata, update existing
            dedupeMap.set(key, {
              ...existing,
              symbol: token.symbol,
              name: token.name || existing.name,
              logoURI: token.logoURI || existing.logoURI,
            });
          }
        } else {
          dedupeMap.set(key, { ...token });
        }
      });

      // Final pass: resolve any missing Solana logos directly from featuredProjects
      // This is a safety net for mobile where the async patching effect might not trigger reliably
      const dbProjects = featuredProjects?.dbProjects;
      const jupiterCache = jupiterTokenListCache.tokens;
      if (dbProjects || jupiterCache) {
        dedupeMap.forEach((token, key) => {
          if (token.network !== "Solana" || token.logoURI) return;

          // Try featured projects first
          if (dbProjects) {
            const featured = dbProjects.find(
              p => p.chainId === SOLANA_CHAIN_ID && p.tokenAddress === token.address
            );
            if (featured?.tokenLogoURI) {
              dedupeMap.set(key, { ...token, logoURI: featured.tokenLogoURI });
              return;
            }
          }

          // Try Jupiter cache
          if (jupiterCache) {
            const jupiterToken = jupiterCache.get(token.address);
            if (jupiterToken?.logoURI) {
              dedupeMap.set(key, {
                ...token,
                logoURI: jupiterToken.logoURI,
                symbol: token.symbol?.startsWith("0x") ? jupiterToken.symbol || token.symbol : token.symbol,
                name: token.name?.startsWith("0x") ? jupiterToken.name || token.name : token.name,
              });
            }
          }
        });
      }

      return Array.from(dedupeMap.values());
    } catch (error) {
      console.error("Error fetching available tokens:", error);
      return [];
    }
  }, [onChainEvmTokens, onChainSolanaTokens, profileBalance, fromChain.chainType, featuredProjects?.dbProjects, jupiterListReady, solanaWalletAddress, evmWalletAddress, onChainNativeSolBalance]);

  useEffect(() => {
    let cancelled = false;

    const fetchFallbackPrices = async () => {
      const missingPriceMints = mergedTokens
        .filter(
          (token) =>
            token.network === "Solana" &&
            !!token.address &&
            Number(token.balance) > SPL_FALLBACK_MIN_BALANCE &&
            token.price <= 0,
        )
        .map((token) => token.address);

      const uniqueMints = [...new Set(missingPriceMints)];
      const requestKey = uniqueMints.sort().join(",");

      if (!requestKey || requestKey === lastJupiterRequestKeyRef.current) {
        return;
      }

      lastJupiterRequestKeyRef.current = requestKey;
      const prices = await fetchJupiterPrices(uniqueMints);

      if (!cancelled) {
        setJupiterFallbackPrices((current) => {
          if (Object.keys(prices).length === 0) return current;
          const hasChanges = Object.entries(prices).some(
            ([key, value]) => current[key] !== value
          );
          if (!hasChanges) return current;
          return { ...current, ...prices };
        });
      }
    };

    fetchFallbackPrices();

    return () => {
      cancelled = true;
    };
  }, [mergedTokens]);

  const availableTokens = useMemo(() => {
    try {
      // Silent diagnostic — log at start of memo run
      logDiagnostic({ event: "memo-run", overrideKeys: Object.keys(onChainOverrides), overrideCount: Object.keys(onChainOverrides).length });

      const priceResolvedTokens = mergedTokens
        .map((token) => {
          if (!token) return null;

          const jupiterPrice = token.address
            ? jupiterFallbackPrices[token.address]
            : undefined;

          if (((token.price ?? 0) <= 0 || (token.balanceUSD ?? 0) <= 0) && jupiterPrice) {
            const numericBalance = Number(token.balance) || 0;
            const balanceUSD = numericBalance * jupiterPrice;

            if (balanceUSD < MIN_USD_BALANCE && token.network === "Solana") {
              return null;
            }

            return {
              ...token,
              price: jupiterPrice,
              balanceUSD,
            };
          }

          if (
            token.network === "Solana" &&
            (token.price ?? 0) > 0 &&
            (token.balanceUSD ?? 0) < MIN_USD_BALANCE
          ) {
            return null;
          }

          return token;
        })
        .filter(Boolean) as TokenBalance[];

      // Apply on-chain balance overrides for recently swapped tokens
      // USD is always computed from Para's current price (prices are reliable, quantities flap)
      const appliedForLog: Array<{ symbol: string; overrideBalance: string; paraBalance: string }> = [];
      const ZERO_ADDR = "0x0000000000000000000000000000000000000000";
      const overriddenTokens = priceResolvedTokens.map((token) => {
        // Normalize native token addresses: Para returns "" while wagmi returns zeroAddress.
        // Overrides are stored under zeroAddress for natives, so normalize lookups to match.
        const rawAddr = (token.address || "").toLowerCase();
        const normalizedAddr = (rawAddr === "" || rawAddr === ZERO_ADDR) ? ZERO_ADDR : rawAddr;
        const key = `${token.evmChainId}-${normalizedAddr}`;
        const override = onChainOverrides[key];
        if (override && override.lastReadAt > 0) {
          const balanceUSD = parseFloat(override.balance) * (token.price ?? 0);
          if (process.env.NODE_ENV !== "production") {
            console.log(
              `🔬 [Override] Using on-chain value for ${token.symbol}: balance=${override.balance}, usd=${balanceUSD.toFixed(2)}`
            );
          }

          // Collect for silent diagnostic batch log
          appliedForLog.push({ symbol: token.symbol, overrideBalance: override.balance, paraBalance: token.balance });

          // Throttled debug log: only log if state changed since last render
          const logKey = `${token.symbol}-${token.evmChainId}`;
          const logState = `${override.balance}|${token.balance}`;
          if (lastLoggedOverrideRef.current.get(logKey) !== logState) {
            lastLoggedOverrideRef.current.set(logKey, logState);
            mobileDebugLog("INJECT", `${token.symbol}`, {
              ovr: override.balance,
              para: token.balance,
              diff: parseFloat(override.balance) !== parseFloat(token.balance || "0"),
            });
          }

          return {
            ...token,
            balance: override.balance,
            balanceUSD,
          };
        }
        return token;
      });

      // Silent diagnostic — batch log all applied overrides for this memo run
      if (appliedForLog.length > 0) {
        logDiagnostic({ event: "memo-applied-batch", applied: appliedForLog });
      }

      return overriddenTokens.sort((a, b) => {
        if (a.isNative && !b.isNative) return -1;
        if (!a.isNative && b.isNative) return 1;
        return (b.balanceUSD ?? 0) - (a.balanceUSD ?? 0);
      });
    } catch (error) {
      console.error("Error processing available tokens:", error);
      return [];
    }
  // renderTick included to ensure re-render when bumpRenderTick() is called (mobile Safari fix)
  }, [jupiterFallbackPrices, mergedTokens, onChainOverrides, renderTick]);

  return {
    availableTokens,
    isLoading: isLoadingProfileBalance,
  };
};
